import { TagDto } from "@closerplatform/spinner-openapi";
import { Instance, types, flow } from "mobx-state-tree";
import { isSystemTag } from "@fidget/common/models/tags";
import { logger } from "@fidget/common/utils";
import { servicesGetters } from "../utils";
import { TagsStoreEventListener } from "./eventHandler";
import { notEmpty } from "@fidget/common/utils/not-empty";

const Tag = types.model("Tag", {
  id: types.identifier,
  name: types.string,
  enabled: types.boolean,
  required: types.boolean,
  priority: types.boolean,
  showInConversationDetails: types.boolean
});

export const TagsStore = types
  .model("TagsStore", {
    orgTagsMap: types.map(Tag)
  })
  .views(servicesGetters)
  .views((self) => {
    const views = {
      isTagOrgDefined(id: string): boolean {
        return self.orgTagsMap.has(id);
      },

      get orgTags(): ITag[] {
        return [...self.orgTagsMap.values()];
      },

      get orgTagsWithoutSystem(): ITag[] {
        return views.orgTags.filter((tag) => !isSystemTag(tag.name));
      },

      get assignableTags(): ITag[] {
        return views.orgTagsWithoutSystem.filter((tag) => tag.enabled);
      },

      filterDisplayableTags(tags?: string[]): ITag[] {
        return tags
          ? views.orgTags.filter((tag) => tag.showInConversationDetails && tags.includes(tag.name))
          : [];
      },

      getById(id: string): ITag | undefined {
        return self.orgTagsMap.get(id);
      },

      getFromIds(ids: string[]): ITag[] {
        return ids.map((id) => self.orgTagsMap.get(id)).filter(notEmpty);
      },

      getWithoutIds(ids: string[]): ITag[] {
        return views.orgTags.filter((tag) => !ids.includes(tag.id));
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      setTags(tags: TagDto[]) {
        self.orgTagsMap.clear();
        tags
          .map((tag) => Tag.create({ ...tag, name: tag.tag }))
          .forEach((t) => self.orgTagsMap.put(t));
      },

      addTag(tag: TagDto) {
        self.orgTagsMap.set(tag.id, Tag.create({ ...tag, name: tag.tag }));
      },

      updateTag(tag: TagDto) {
        self.orgTagsMap.set(tag.id, { ...tag, name: tag.tag });
      },

      deleteTag(id: string) {
        self.orgTagsMap.delete(id);
      },

      loadTags: flow(function* () {
        try {
          const tags: TagDto[] = yield self.spinnerService.getTags();
          actions.setTags(tags);
        } catch (err) {
          logger.error({ message: "Couldn't load org tags", error: err });
        }
      })
    };

    return actions;
  })
  .actions((self) => {
    const eventListenerName = "TagsStoreEventListener";
    const actions = {
      afterCreate() {
        self.eventListener.addGlobalListener(
          new TagsStoreEventListener(() => self as ITagsStore, eventListenerName)
        );
      },

      beforeDestroy() {
        self.eventListener.removeGlobalListener(eventListenerName);
      }
    };

    return actions;
  });

export interface ITag extends Instance<typeof Tag> {}
export interface ITagsStore extends Instance<typeof TagsStore> {}
